import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { X, Waves } from "lucide-react";
import { toast } from "sonner";

function fmtErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export function AuthModal({ open, onClose, onSuccess, reason }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "register"
        ? await register({ name: form.name, email: form.email, password: form.password, phone: form.phone })
        : await login(form.email, form.password);
      toast.success(mode === "register" ? "Account created — you can bid now!" : `Welcome back, ${u.name || ""}`);
      onSuccess?.(u);
      onClose?.();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-void/85 backdrop-blur-sm flex items-center justify-center p-5" onClick={onClose}>
          <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="glass rounded-3xl p-8 w-full max-w-md border border-cyan/20" data-testid="auth-modal">
            <div className="flex justify-between items-start mb-2">
              <Waves className="text-cyan" size={26} />
              <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <h2 className="font-display text-3xl mb-1">{mode === "register" ? "Create your account" : "Welcome back"}</h2>
            <p className="text-white/50 text-sm mb-6">{reason || "Register to bid on corals & fish. It only takes a moment."}</p>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <input className="er-input" placeholder="Full name" data-testid="auth-name" value={form.name} onChange={set("name")} />
              )}
              <input type="email" required className="er-input" placeholder="Email" data-testid="auth-email" value={form.email} onChange={set("email")} />
              <input type="password" required className="er-input" placeholder="Password (min 6 characters)" data-testid="auth-password" value={form.password} onChange={set("password")} />
              {mode === "register" && (
                <div>
                  <input className="er-input" placeholder="Mobile number — get SMS bid & win alerts" data-testid="auth-phone" value={form.phone} onChange={set("phone")} />
                  <p className="text-white/35 text-xs mt-1.5 font-mono">Add your mobile to get texted when you're outbid or win an auction.</p>
                </div>
              )}
              <button disabled={busy} data-testid="auth-submit" className="btn-pill btn-solid w-full">
                {busy ? "Please wait…" : mode === "register" ? "Create account & bid" : "Sign in"}
              </button>
            </form>

            <p className="text-white/50 text-sm text-center mt-5">
              {mode === "register" ? "Already have an account?" : "New here?"}{" "}
              <button data-testid="auth-toggle" onClick={() => setMode(mode === "register" ? "login" : "register")} className="text-cyan hover:underline">
                {mode === "register" ? "Sign in" : "Create an account"}
              </button>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
